import React, { useMemo, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Header } from './src/components/Header';
import { KpiSection } from './src/components/KpiSection';
import { LeaderboardSection } from './src/components/LeaderboardSection';
import { TrendsSection } from './src/components/TrendsSection';
import { PerformanceTable } from './src/components/PerformanceTable';
import { SnapshotHistory } from './src/components/SnapshotHistory';
import { SnapshotConfirmModal } from './src/components/SnapshotConfirmModal';
import { SnapshotDetailModal } from './src/components/SnapshotDetailModal';
import { CompareModal } from './src/components/CompareModal';
import { AvpDetailModal } from './src/components/AvpDetailModal';
import { mockAVPs, mockKPIs, mockSnapshots, mockTrendData, historicalLeaderboards, getLoanOfficersForAvp } from './src/data/mockData';
import { AVP, Snapshot } from './src/types';
const toastStyle = {
  background: '#0f1423',
  border: '1px solid rgba(129, 140, 248, 0.25)',
  color: '#fff'
};
export function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSnapshot, setActiveSnapshot] = useState<Snapshot | null>(null);
  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const [activeAvp, setActiveAvp] = useState<AVP | null>(null);
  // Build comparison periods: current month + all finalized snapshots
  const comparePeriods = useMemo(() => [{
    id: 'current',
    label: 'April 2026 (Current)',
    leaderboard: mockAVPs
  }, ...mockSnapshots.map(s => ({
    id: s.id,
    label: `${s.month} ${s.year}`,
    leaderboard: historicalLeaderboards[s.id] || []
  }))], []);
  const handleStampSnapshot = () => {
    setIsModalOpen(false);
    toast.success('Snapshot Stamped Successfully', {
      description: 'April 2026 performance records have been permanently preserved.',
      icon: '🔒',
      style: {
        background: '#0f1423',
        border: '1px solid rgba(52, 211, 153, 0.25)',
        color: '#fff'
      }
    });
  };
  const handleViewHistorical = () => {
    document.getElementById('snapshot-history')?.scrollIntoView({
      behavior: 'smooth'
    });
    toast('Jumping to historical snapshots', {
      description: 'Browse all finalized monthly records below.',
      icon: '📜',
      style: toastStyle
    });
  };
  const handleCompare = () => {
    setIsCompareOpen(true);
  };
  const handleExport = () => {
    toast.promise(new Promise(resolve => setTimeout(resolve, 1500)), {
      loading: 'Generating export…',
      success: 'Rankings exported as CSV',
      error: 'Export failed',
      style: toastStyle
    });
  };
  const handleViewAllSnapshots = () => {
    document.getElementById('snapshot-history')?.scrollIntoView({
      behavior: 'smooth'
    });
    toast('All Snapshots', {
      description: 'Showing every finalized monthly record.',
      icon: '🗂️',
      style: toastStyle
    });
  };
  const handleOpenSnapshot = (snap: Snapshot) => {
    setActiveSnapshot(snap);
  };
  return <div className="min-h-screen bg-[#0a0e1a] text-slate-200 selection:bg-indigo-500/30" data-id="element-0">
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" data-id="element-1">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-600/10 blur-[120px]" data-id="element-2" />
        <div className="absolute top-[20%] right-[-10%] w-[30%] h-[30%] rounded-full bg-violet-600/10 blur-[100px]" data-id="element-3" />
        <div className="absolute bottom-[-10%] left-[20%] w-[40%] h-[40%] rounded-full bg-emerald-600/5 blur-[120px]" data-id="element-4" />
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5">
        <Header onStampSnapshot={() => setIsModalOpen(true)} onViewHistorical={handleViewHistorical} onCompare={handleCompare} onExport={handleExport} data-id="element-6" />

        <main data-id="element-7">
          <KpiSection kpis={mockKPIs} data-id="element-8" />
          <LeaderboardSection avps={mockAVPs} onSelectAvp={setActiveAvp} data-id="element-9" />
          <TrendsSection data={mockTrendData} data-id="element-10" />
          <PerformanceTable avps={mockAVPs} data-id="element-11" />
          <SnapshotHistory snapshots={mockSnapshots} onViewAll={handleViewAllSnapshots} onOpenSnapshot={handleOpenSnapshot} data-id="element-12" />
        </main>
      </div>

      <SnapshotConfirmModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={handleStampSnapshot} month="April" year={2026} avpCount={mockAVPs.length} data-id="element-13" />

      <SnapshotDetailModal isOpen={activeSnapshot !== null} onClose={() => setActiveSnapshot(null)} snapshot={activeSnapshot} leaderboard={activeSnapshot ? historicalLeaderboards[activeSnapshot.id] || [] : []} data-id="element-14" />

      <CompareModal isOpen={isCompareOpen} onClose={() => setIsCompareOpen(false)} periods={comparePeriods} data-id="element-15" />

      <AvpDetailModal isOpen={activeAvp !== null} onClose={() => setActiveAvp(null)} avp={activeAvp} loanOfficers={activeAvp ? getLoanOfficersForAvp(activeAvp) : []} data-id="element-16" />

      <Toaster position="top-right" theme="dark" data-id="element-17" />
    </div>;
}